import * as React from 'react';
import { useState, useRef, createContext, useEffect, useCallback } from 'react';
import { Chat, Message, PageContext, PageType, EntityContext, FroalaEditorInstance, froalaEditorRegistry } from './types';
import { ChatStore, localStorageChatStore } from './chatStore';

// Helper to generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper to generate chat title from first message
const generateTitle = (messages: Message[]): string => {
  if (messages.length === 0) return 'New Chat';
  const firstUserMessage = messages.find(m => m.role === 'user');
  if (!firstUserMessage) return 'New Chat';
  const content = firstUserMessage.content;
  return content.length > 30 ? content.substring(0, 30) + '...' : content;
};

// Helper to derive page type from path
export const derivePageTypeFromPath = (path: string): { pageType: PageType; section?: string } => {
  const segments = path.split('/').filter(Boolean);
  
  // Dashboard
  if (path === '/' || path === '/dashboard') {
    return { pageType: 'dashboard' };
  }
  
  // Analytics
  if (path === '/analytics') {
    return { pageType: 'analytics' };
  }
  
  // Taxonomy
  if (path === '/taxonomy') {
    return { pageType: 'taxonomy' };
  }
  
  // Workflow
  if (path.startsWith('/workflow')) {
    return { pageType: 'workflow' };
  }
  
  // Editorial section
  if (segments[0] === 'editorial') {
    const subSection = segments[1];
    // IDs can be negative (locally seeded content uses negative oids)
    const hasId = segments.length > 2 && /^-?\d+$/.test(segments[2]);
    
    switch (subSection) {
      case 'articles':
        return { 
          pageType: hasId ? 'article-edit' : 'article-list', 
          section: 'editorial' 
        };
      case 'pages':
        return { 
          pageType: hasId ? 'page-edit' : 'page-list', 
          section: 'editorial' 
        };
      case 'newsletters':
        return { 
          pageType: hasId ? 'newsletter-edit' : 'newsletter-list', 
          section: 'editorial' 
        };
      case 'publications':
        return { 
          pageType: hasId ? 'publication-edit' : 'publication-list', 
          section: 'editorial' 
        };
      // /editorial (the Editorial nav link) renders the article list too.
      case undefined:
        return { pageType: 'article-list', section: 'editorial' };
      default:
        return { pageType: 'unknown', section: 'editorial' };
    }
  }
  
  // Revenue section
  if (segments[0] === 'revenue') {
    const subSection = segments[1];
    const hasId = segments.length > 2 && /^-?\d+$/.test(segments[2]);
    
    switch (subSection) {
      case 'events':
        return { 
          pageType: hasId ? 'event-edit' : 'event-list', 
          section: 'revenue' 
        };
      case 'directory':
        return { 
          pageType: hasId ? 'directory-edit' : 'directory-list', 
          section: 'revenue' 
        };
      case 'products':
        return { 
          pageType: hasId ? 'product-edit' : 'product-list', 
          section: 'revenue' 
        };
      case 'orders':
        return { 
          pageType: hasId ? 'order-edit' : 'order-list', 
          section: 'revenue' 
        };
      default:
        return { pageType: 'unknown', section: 'revenue' };
    }
  }
  
  // Users
  if (segments[0] === 'users') {
    const hasId = segments.length > 1 && /^-?\d+$/.test(segments[1]);
    return { 
      pageType: hasId ? 'user-edit' : 'user-list' 
    };
  }
  
  // Media
  if (segments[0] === 'media') {
    return { pageType: 'media' };
  }
  
  // Advanced/Settings
  if (segments[0] === 'advanced') {
    return { pageType: 'settings', section: 'advanced' };
  }
  
  return { pageType: 'unknown' };
};

// Context type
interface AISearchContextType {
  // Drawer state
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  
  // Chat management
  chats: Chat[];
  /** False until the chat store has answered; history can be empty before then. */
  chatsLoaded: boolean;
  activeChatId: string | null;
  activeChat: Chat | null;
  
  // Chat actions
  createNewChat: () => string;
  switchChat: (chatId: string) => void;
  deleteChat: (chatId: string) => void;
  
  // Message actions
  addMessage: (message: Message, chatId?: string) => void;
  updateLastMessage: (updates: Partial<Message>, chatId?: string) => void;
  
  // Typing state
  isTyping: boolean;
  setIsTyping: (isTyping: boolean) => void;
  
  // Page context
  pageContext: PageContext | null;
  setPageContext: (
    context: PageContext | null | ((prev: PageContext | null) => PageContext | null)
  ) => void;
  updatePageContext: (updates: Partial<PageContext>) => void;
  setEntityContext: (entity: EntityContext | undefined) => void;

  // Froala editor integration
  froalaEditor: FroalaEditorInstance | null;
  registerFroalaEditor: (editor: FroalaEditorInstance | null) => void;
}

export const Context = createContext<AISearchContextType>({
  isDrawerOpen: false,
  setIsDrawerOpen: () => {},
  chats: [],
  chatsLoaded: true,
  activeChatId: null,
  activeChat: null,
  createNewChat: () => '',
  switchChat: () => {},
  deleteChat: () => {},
  addMessage: () => {},
  updateLastMessage: () => {},
  isTyping: false,
  setIsTyping: () => {},
  pageContext: null,
  setPageContext: () => {},
  updatePageContext: () => {},
  setEntityContext: () => {},
  froalaEditor: null,
  registerFroalaEditor: () => {},
});

interface Props {
  children: React.ReactNode;
  /** Where chat history is kept. Defaults to the browser's localStorage. */
  store?: ChatStore;
}

const AISearchProvider = (props: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const store = props.store || localStorageChatStore;
  const [chats, setChats] = useState<Chat[]>([]);
  // Mirrors of `chats` and `activeChatId` that actions update the moment they
  // run, so two actions in one batch see each other's result.
  const chatsRef = useRef<Chat[]>([]);
  const activeChatIdRef = useRef<string | null>(null);
  const [chatsLoaded, setChatsLoaded] = useState<boolean>(false);
  const [activeChatId, setActiveChatIdState] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [pageContext, setPageContextState] = useState<PageContext | null>(null);

  const updateChats = useCallback((update: (prev: Chat[]) => Chat[]) => {
    const next = update(chatsRef.current);
    chatsRef.current = next;
    setChats(next);
  }, []);

  const setActiveChatId = useCallback((id: string | null) => {
    activeChatIdRef.current = id;
    setActiveChatIdState(id);
  }, []);

  // The version of each chat last sent to the store, by id. Persisting compares
  // against this by reference, so only chats that actually changed are sent. A
  // chat stays here after a failed save: it may still be stored in an older
  // version, so deleting it must still reach the store.
  const persisted = useRef<Map<string, Chat>>(new Map());

  useEffect(() => {
    let cancelled = false;
    store.list()
      .then((stored) => {
        if (cancelled) return;
        stored.forEach((chat) => persisted.current.set(chat.id, chat));
        // Keep any chat started while the store was answering.
        updateChats((prev) => [...prev, ...stored.filter((s) => !prev.some((c) => c.id === s.id))]);
      })
      .catch((error) => console.error('Error loading chats:', error))
      .then(() => !cancelled && setChatsLoaded(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write changes through to the store: save new and edited chats, remove
  // deleted ones. Waits for the first load so it cannot wipe what it has not seen.
  useEffect(() => {
    if (!chatsLoaded) return;
    const known = persisted.current;
    const present = new Set(chats.map((c) => c.id));

    chats.forEach((chat) => {
      // A chat is stored once it has a message; empty ones stay in memory.
      if (chat.messages.length === 0 || known.get(chat.id) === chat) return;
      known.set(chat.id, chat);
      store.save(chat).catch((error) => {
        // The next edit to this chat sends it again; unchanged chats are not
        // re-sent on every unrelated update.
        console.error('Error saving chat:', error);
      });
    });
    Array.from(known.keys()).forEach((id) => {
      if (present.has(id)) return;
      const chat = known.get(id) as Chat;
      known.delete(id);
      store.remove(id).catch((error) => {
        console.error('Error deleting chat:', error);
        // Still stored, so retry the removal on the next change.
        if (!known.has(id)) known.set(id, chat);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, chatsLoaded]);

  // Get active chat
  const activeChat = chats.find(c => c.id === activeChatId) || null;

  // Create a new chat
  const createNewChat = useCallback((): string => {
    // Reuse an empty chat instead of piling up blank ones.
    const empty = chatsRef.current.find((c) => c.messages.length === 0);
    if (empty) {
      setActiveChatId(empty.id);
      return empty.id;
    }
    const newChat: Chat = {
      id: generateId(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    updateChats(prev => [newChat, ...prev]);
    setActiveChatId(newChat.id);
    return newChat.id;
  }, [updateChats, setActiveChatId]);

  // Switch to a different chat. Blank chats the user is leaving are dropped;
  // they were never saved.
  const switchChat = useCallback((chatId: string) => {
    updateChats(prev => prev.filter(c => c.id === chatId || c.messages.length > 0));
    setActiveChatId(chatId);
  }, [updateChats, setActiveChatId]);

  // Delete a chat
  const deleteChat = useCallback((chatId: string) => {
    const newChats = chatsRef.current.filter(c => c.id !== chatId);
    updateChats(() => newChats);
    // If we deleted the active chat, switch to another or clear
    if (activeChatIdRef.current === chatId) {
      const next = newChats.find(c => c.messages.length > 0) || newChats[0];
      setActiveChatId(next ? next.id : null);
    }
  }, [updateChats, setActiveChatId]);

  // Add a message to a chat (uses the active chat if chatId not provided)
  const addMessage = useCallback((message: Message, chatId?: string) => {
    const targetChatId = chatId || activeChatIdRef.current;
    if (!targetChatId) return;

    updateChats(prev => prev.map(chat => {
      if (chat.id !== targetChatId) return chat;
      
      const newMessages = [...chat.messages, message];
      return {
        ...chat,
        messages: newMessages,
        title: generateTitle(newMessages),
        updatedAt: new Date(),
      };
    }));
  }, [updateChats]);

  // Update the last message in a chat (uses the active chat if chatId not provided)
  const updateLastMessage = useCallback((updates: Partial<Message>, chatId?: string) => {
    const targetChatId = chatId || activeChatIdRef.current;
    if (!targetChatId) return;

    updateChats(prev => prev.map(chat => {
      if (chat.id !== targetChatId) return chat;
      if (chat.messages.length === 0) return chat;
      
      const newMessages = [...chat.messages];
      const lastIndex = newMessages.length - 1;
      newMessages[lastIndex] = { ...newMessages[lastIndex], ...updates };
      
      return {
        ...chat,
        messages: newMessages,
        updatedAt: new Date(),
      };
    }));
  }, [updateChats]);

  // Set page context (full replacement)
  const setPageContext = useCallback((
    context: PageContext | null | ((prev: PageContext | null) => PageContext | null)
  ) => {
    setPageContextState(context);
  }, []);

  // Update page context (partial update)
  const updatePageContext = useCallback((updates: Partial<PageContext>) => {
    setPageContextState(prev => {
      if (!prev) return null;
      return { ...prev, ...updates };
    });
  }, []);

  // Register/unregister a Froala editor instance for link insertion
  const registerFroalaEditor = useCallback((editor: FroalaEditorInstance | null) => {
    froalaEditorRegistry.current = editor;
  }, []);

  // Set just the entity context
  const setEntityContext = useCallback((entity: EntityContext | undefined) => {
    setPageContextState(prev => {
      if (!prev) return null;
      return { ...prev, entity };
    });
  }, []);

  const value: AISearchContextType = {
    isDrawerOpen,
    setIsDrawerOpen,
    chats,
    chatsLoaded,
    activeChatId,
    activeChat,
    createNewChat,
    switchChat,
    deleteChat,
    addMessage,
    updateLastMessage,
    isTyping,
    setIsTyping,
    pageContext,
    setPageContext,
    updatePageContext,
    setEntityContext,
    froalaEditor: froalaEditorRegistry.current,
    registerFroalaEditor,
  };

  return (
    <Context.Provider value={value}>
      {props.children}
    </Context.Provider>
  );
};

export default AISearchProvider;